import { AGGREGATION_SELECT_OPTIONS } from '../config/config';
import { v4 as uuid } from 'uuid';
import { timeFrameOptions } from './timeFrameToDateUtils';
import dayjs from 'dayjs';
import { tagListItem } from '../config/pdfReportConfig';

const mapDataCustomColumns = (column: any, index: number) => ({
  aggregation:
    column.aggregation === null
      ? null
      : AGGREGATION_SELECT_OPTIONS.find((aggr) => aggr.value === column.aggregation.toLowerCase()),
  order: index,
  id: uuid(),
  unit: column.unit,
  tag: {
    tagId: column.tagid,
    rawUnit: column.unit,
    tagName: column.tagname,
    gatewayId: column.gatewayid,
    siteId: column.siteid,
    siteName: column.sitename,
  },

  columnStatistics: column.statistics
    ? {
        TOTAL: column.statistics.includes('TOTAL'),
        MIN: column.statistics.includes('MIN'),
        MAX: column.statistics.includes('MAX'),
        AVERAGE: column.statistics.includes('AVERAGE'),
      }
    : { TOTAL: false, MIN: false, MAX: false, AVERAGE: false },
});

const mapTagList = (tagList: []) => {
  let res: any = [];
  tagList.forEach((tag: tagListItem, index) => {
    const item = {
      inputId: tag.inputId,
      gatewayId: tag.gatewayId,
      rawInputName: tag.rawInputName,
      unit: tag.unit,
      order: index,
    };
    res.push(item);
  });
  return res;
};

const mapAssetCustomColumns = (assetProps: any) => (column: any, index: number) => ({
  asset: {
    type: column.assetType,
    property: assetProps.find((el: any) => el.value === column.name),
  },
  id: uuid(),
  order: index,
  aggregation: AGGREGATION_SELECT_OPTIONS.find(
    (aggr) => aggr.value === column.aggregation.toLowerCase()
  ),
  columnStatistics: column.statistics
    ? {
        TOTAL: column.statistics.includes('TOTAL'),
        MIN: column.statistics.includes('MIN'),
        MAX: column.statistics.includes('MAX'),
        AVERAGE: column.statistics.includes('AVERAGE'),
      }
    : { TOTAL: false, MIN: false, MAX: false, AVERAGE: false },
});
const parseTimeFrame = (template: any) => {
  const element = timeFrameOptions.find((option) => {
    const dateRange = option.parseToDate();

    return (
      dayjs(dateRange[0]).format('YYYY-MM-DD') === template.timeframe.start &&
      dayjs(dateRange[1]).format('YYYY-MM-DD') === template.timeframe.end
    );
  });

  if (element) return element.value;
  return [template.timeframe.start, template.timeframe.end];
};
const findInitalSiteId = (sites: any[], siteId: string) =>
  sites.find((site) => site.value === siteId);

const findInitialAssets = (
  assets: { loaded: boolean; data: { label: string; value: string }[] },
  assetIds: string[]
) => {
  if (!assets || !assets.data || !assetIds || !assetIds.length) {
    return [];
  }

  return assetIds.map((id) => assets.data.find((asset: any) => asset.value === id)).filter(Boolean);
};

const parsePDFTemplateResponse = (
  template: any,
  timezone: any,
  filteredCustomerSites?: any,
  assets?: any
) => ({
  timeFrame: parseTimeFrame(template),
  reportTemplate: template.reportTemplate || null,
  siteId: findInitalSiteId(filteredCustomerSites, template.siteId) || null,
  dataInterval: String(template.dataInterval) || null,
  dataAggregation: template.dataAggregation || null,
  outlierTolerance: String(template.outlierTolerance) || null,
  mlssPerWeek: String(template.mlssPerWeek) || null,
  assetIds: assets && template.assetIds ? findInitialAssets(assets, template.assetIds) : [],
  timeZone: timezone,
  tolerance: template.tolerance ?? '',
  columns: {
    default: {},
    custom: [],
  },
  filters: null,
});

const parsePDFSiteTemplateResponse = (template: any, timezone: any, sites?: any, tags?: any) => ({
  timeFrame: parseTimeFrame(template),
  reportTemplate: template.reportTemplate || null,
  siteId: findInitalSiteId(sites, template.siteId) || null,
  tolerance: String(template.tolerance) || null,
  timeZone: timezone,
  tagList: mapTagList(template.tagList),
});
// template.tagList

const parseTemplateResponse = (template: any, timezone: any, assetProps?: any, filters?: any) => ({
  timeFrame: parseTimeFrame(template),
  timeZone: timezone,
  filters: template.filters,
  generateAiSummary: template.reportType === 'ASSET' ? !!template.generateAiSummary : false,
  columns: {
    default:
      template.reportType === 'DATA'
        ? {
            date: template.columns.date,
            time: template.columns.time,
          }
        : {
            date: template.columns.default.date,
            site_name: template.columns.default.siteName,
            site_type: template.columns.default.siteType,
            asset_name: template.columns.default.assetName,
            asset_type: template.columns.default.assetType,
          },

    custom: template.columns.custom
      ? template.columns.custom.map(
          template.reportType === 'ASSET'
            ? mapAssetCustomColumns(assetProps.reduce((acc: any, ap: any) => acc.concat(ap[1]), []))
            : mapDataCustomColumns
        )
      : [],
  },
  aggregationInterval: template.dataAggregation,
});

export { parseTemplateResponse, parsePDFTemplateResponse, parsePDFSiteTemplateResponse };
